import React, { useEffect, useRef, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useSelector, useDispatch } from 'react-redux';

import { Button, Modal, SliderWrapper } from '@/components/ui';
import { CloseMobile } from '@/components/SvgComponents';
import { GalleryArrow } from '@/components/SvgComponents/GalleryArrow';
import { Reminder } from '@/components/SvgComponents/Reminder';
import { selectors, actions } from '@/domains/mini-market';
import { extractItemId } from '@/constants/helper';

import { DeviceImages } from '../DeviceImages';

import styles from './index.module.scss';

export const ImageModal = ({
  modalActive,
  isMobileView,
  device,
  setModalActive,
  setReminderModalActive,
}) => {
  const dispatch = useDispatch();
  const { selectedImage, buttonDisabled } = useSelector(
    selectors.selectMiniMarket,
  );
  const router = useRouter();
  const { t } = useTranslation();
  const scrollableDivRef = useRef(null);
  const [initialized, setInitialized] = useState(false);
  const handleNextArrowClick = () => {
    if (scrollableDivRef.current && !buttonDisabled) {
      const currentIndex = device?.album?.photos?.findIndex(
        (i) => i === selectedImage,
      );
      if (currentIndex !== device?.album?.photos?.length - 1) {
        dispatch(
          actions.setSelectedImage(device?.album?.photos[currentIndex + 1]),
        );
      }
      const scrollContainer = scrollableDivRef.current;
      const selectedImageElement = scrollContainer.querySelector(
        `[dataImageId='${selectedImage}']`,
      );
      if (selectedImageElement) {
        const scrollContainerRect = scrollContainer.getBoundingClientRect();
        const selectedImageRect = selectedImageElement.getBoundingClientRect();
        if (selectedImageRect.right > scrollContainerRect.right) {
          scrollContainer.scrollBy({
            left: 160,
            behavior: 'smooth',
          });
        }
      }
    }
  };

  const handlePrevArrowClick = () => {
    if (scrollableDivRef.current && !buttonDisabled) {
      const currentIndex = device?.album?.photos?.findIndex(
        (i) => i === selectedImage,
      );
      if (currentIndex !== 0) {
        dispatch(
          actions.setSelectedImage(device?.album?.photos[currentIndex - 1]),
        );
      }
      const scrollContainer = scrollableDivRef.current;
      const selectedImageElement = scrollContainer.querySelector(
        `[dataImageId='${selectedImage}']`,
      );
      if (selectedImageElement) {
        const scrollContainerRect = scrollContainer.getBoundingClientRect();
        const selectedImageRect = selectedImageElement.getBoundingClientRect();
        if (selectedImageRect.left < scrollContainerRect.left) {
          scrollContainer.scrollBy({
            left: -160,
            behavior: 'smooth',
          });
        }
      }
    }
  };
  const sliderRef = useRef(null);
  const index = device?.album?.photos?.findIndex(
    (image) => image === selectedImage,
  );
  useEffect(() => {
    sliderRef.current.slickGoTo(index);
  }, [index]);
  const reminderHandler = () => {
    setReminderModalActive(true);
    setModalActive(false);
  };

  const routeToOrder = () => {
    router.push({
      pathname: '/order',
      query: {
        itemId: router.query.dynamicPage[0],
        webview: router.query.webview,
      },
    });
  };

  return (
    <Modal
      className={styles.customModal}
      active={modalActive}
      itemScope
      itemType='https://schema.org/Thing'
    >
      <div className={styles.top}>
        <div className={styles.firstTitle}>{device?.album?.name}</div>
        <div className={styles.right}>
          {!isMobileView && (
            <>
              <div className={styles.modalPrice}>
                {device?.new_price
                  ? device?.old_price && (
                      <>
                        {' '}
                        <p className={styles.discountPrice}>
                          {device?.old_price}₼
                        </p>
                        <p className={styles.price}>{device?.new_price} ₼</p>
                      </>
                    )
                  : device?.old_price && (
                      <p className={styles.price}>{device?.old_price} ₼</p>
                    )}
              </div>
              <div className={styles.btn}>
                {device?.in_stock ? (
                  <Button onClick={() => routeToOrder()} id='testingId'>
                    {t('common:OrderNow')}
                  </Button>
                ) : (
                  <Button onClick={reminderHandler} variant='outline'>
                    <Reminder /> {t('common:RemindMe')}
                  </Button>
                )}
              </div>
            </>
          )}

          <CloseMobile onClick={() => setModalActive(false)} />
        </div>
      </div>
      <div className={styles.bottom}>
        <div className={styles.imageWrapper}>
          <div className={styles.mainImage}>
            <SliderWrapper
              className={styles.customSlider}
              arrows={false}
              options={{
                infinite: false,
                slidesToShow: 1,
                autoplay: false,
                dots: false,
                speed: 300,
                afterChange: (index) => {
                  dispatch(
                    actions.setSelectedImage(device?.album?.photos?.[index]),
                  );
                  dispatch(actions.setButtonDisabled(false));
                  setInitialized(true);
                },
                beforeChange: () => {
                  if (initialized) {
                    dispatch(actions.setButtonDisabled(true));
                  }
                },

                responsive: [
                  {
                    breakpoint: 1025,
                    settings: {
                      infinite: false,
                      arrows: false,
                      dots: false,
                      slidesToShow: 1,
                    },
                  },
                  {
                    breakpoint: 767,
                    settings: {
                      infinite: false,
                      arrows: false,
                      dots: false,
                      slidesToShow: 1,
                    },
                  },
                ],
              }}
              ref={sliderRef}
            >
              {device?.album?.photos.map((image) => {
                return (
                  <div key={image}>
                    {selectedImage && (
                      <Image
                        className={styles.sliderImage}
                        src={selectedImage}
                        alt={device?.album?.name}
                        title={device?.album?.name}
                        width={590}
                        height={590}
                      />
                    )}
                  </div>
                );
              })}
            </SliderWrapper>
          </div>
          <div className={styles.images}>
            {!isMobileView && (
              <GalleryArrow
                onClick={handlePrevArrowClick}
                className={styles.prev}
              />
            )}

            <div className={styles.overflowWrapper} ref={scrollableDivRef}>
              <DeviceImages images={device?.album?.photos} />
            </div>
            {!isMobileView && (
              <GalleryArrow
                onClick={handleNextArrowClick}
                className={styles.next}
              />
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
